const WINDOW_ACTIVITY_STORAGE_PREFIX = 'messenger-v2-window-activity';
const WINDOW_ACTIVITY_TTL_MS = 60000;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const buildWindowActivityStorageKey = (userId) => {
  const normalizedUserId = toStringOrNull(userId);
  return normalizedUserId ? `${WINDOW_ACTIVITY_STORAGE_PREFIX}:${normalizedUserId}` : null;
};

const readWindowActivityMap = (userId) => {
  const storageKey = buildWindowActivityStorageKey(userId);
  if (!storageKey || typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeWindowActivityMap = (userId, map = {}) => {
  const storageKey = buildWindowActivityStorageKey(userId);
  if (!storageKey || typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return false;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(map));
    return true;
  } catch {
    return false;
  }
};

const pruneStaleWindowActivityEntries = (map = {}) => {
  const now = Date.now();
  const next = {};

  Object.entries(map || {}).forEach(([windowId, entry]) => {
    if (!windowId || !entry || typeof entry !== 'object') {
      return;
    }

    const updatedAt = Number(entry.updatedAt ?? 0);
    if (!Number.isFinite(updatedAt) || now - updatedAt > WINDOW_ACTIVITY_TTL_MS) {
      return;
    }

    next[windowId] = {
      conversationId: toStringOrNull(entry.conversationId ?? null),
      isVisible: entry.isVisible === true,
      updatedAt,
    };
  });

  return next;
};

export const updateMessengerV2WindowActivity = ({
  userId = null,
  windowId = null,
  conversationId = null,
  isVisible = false,
} = {}) => {
  const normalizedUserId = toStringOrNull(userId);
  const normalizedWindowId = toStringOrNull(windowId);
  if (!normalizedUserId || !normalizedWindowId) {
    return false;
  }

  const nextMap = pruneStaleWindowActivityEntries(readWindowActivityMap(normalizedUserId));
  nextMap[normalizedWindowId] = {
    conversationId: toStringOrNull(conversationId ?? null),
    isVisible: isVisible === true,
    updatedAt: Date.now(),
  };
  return writeWindowActivityMap(normalizedUserId, nextMap);
};

export const clearMessengerV2WindowActivity = ({ userId = null, windowId = null } = {}) => {
  const normalizedUserId = toStringOrNull(userId);
  const normalizedWindowId = toStringOrNull(windowId);
  if (!normalizedUserId || !normalizedWindowId) {
    return false;
  }

  const nextMap = readWindowActivityMap(normalizedUserId);
  if (normalizedWindowId in nextMap) {
    delete nextMap[normalizedWindowId];
  }
  return writeWindowActivityMap(normalizedUserId, nextMap);
};

export const isMessengerV2ConversationVisibleInAnyWindow = ({
  userId = null,
  conversationId = null,
  excludeWindowId = null,
} = {}) => {
  const normalizedUserId = toStringOrNull(userId);
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedUserId || !normalizedConversationId) {
    return false;
  }

  const normalizedExcludeWindowId = toStringOrNull(excludeWindowId);
  const nextMap = pruneStaleWindowActivityEntries(readWindowActivityMap(normalizedUserId));

  const isVisible = Object.entries(nextMap).some(([windowId, entry]) => {
    if (normalizedExcludeWindowId && windowId === normalizedExcludeWindowId) {
      return false;
    }
    return (
      entry?.isVisible === true &&
      toStringOrNull(entry?.conversationId ?? null) === normalizedConversationId
    );
  });

  return isVisible;
};

export const resolveMessengerV2WindowVisibility = () => {
  if (typeof document === 'undefined') {
    return false;
  }
  if (document.visibilityState !== 'visible') {
    return false;
  }
  return true;
};
